/**
 * Low-level type owner for the composite (multi-stage, spatially patterned)
 * canonical skill form. Data-only, exactly like `types.ts`: no validation, no
 * expansion, no execution. Every other composite module imports its names
 * from here so the vocabulary has one home.
 *
 * Ratified by `docs/gameplay/canonical-skill-engine-contract.md`
 * (amendment CANON.V3.1); designed in
 * `docs/superpowers/specs/2026-09-18-composite-spatial-skills-design.md`.
 */
import type { CanonicalEffectNodeV2, CanonicalSkillV2Input, SkillApplicationV2 } from './types.js';

export const CANONICAL_SKILL_V3_SCHEMA_VERSION = 'canonical-skill-v3' as const;
export const SPATIAL_ATTACK_PATTERN_V1_SCHEMA_VERSION = 'spatial-attack-pattern-v1' as const;

declare const validatedCanonicalSkillV3: unique symbol;

/**
 * Whether the members of one formation share a single attack unit or each get
 * their own. This is the load-bearing distinction of the whole feature:
 *
 * - `SHARED_UNION` — every member footprint is tested against ONE deduplicated
 *   hit map. A target standing inside all three footprints of a three-way
 *   thrust takes exactly one hit. Its expected value is deduplicated union
 *   coverage, never the sum of the member areas.
 * - `INDEPENDENT` — each member carries its own attack unit and its own hit
 *   record. Five overlapping pellets can hit the same target five times, and
 *   the expected value is the sum of the per-member expectations.
 *
 * The two are never evaluated with the same formula
 * (`skill-value-engine-contract.md` §13.4).
 */
export type HitGroupingV1 = 'SHARED_UNION' | 'INDEPENDENT';

/**
 * Where a stage's members sit in space. A formation places members; it does
 * not by itself decide how many times a target can be hit — that is
 * `hitGrouping`'s job.
 */
export type SpatialFormationV1 =
  | { readonly kind: 'SINGLE'; readonly count: 1 }
  | { readonly kind: 'FAN'; readonly count: number; readonly spreadAngleDeg: number }
  | { readonly kind: 'PARALLEL'; readonly count: number; readonly spacingM: number }
  | { readonly kind: 'RADIAL'; readonly count: number; readonly initialAngleDeg: number }
  | { readonly kind: 'RING'; readonly count: number; readonly radiusM: number; readonly initialAngleDeg: number }
  | { readonly kind: 'SCATTER'; readonly count: number; readonly radiusM: number; readonly minSpacingM?: number }
  /**
   * An attack that, once it strikes an enemy, goes on to the next: `count` nodes, the first included. Its members are not
   * placed at the cast; each one after the first is released from the enemy the chain last struck toward the next enemy
   * it picks within `jumpRangeM` (`pick`, ties to the lower id), and the path never revisits an enemy. The member schedule
   * says when the path is picked: `SEQUENTIAL` picks the next enemy when a member strikes (a ricochet), `SIMULTANEOUS`
   * plans the whole path when the first member strikes and releases every other member that tick (a planned chain).
   * `only` restricts every pick to the carriers of one of this cast's skill statuses.
   */
  | {
      readonly kind: 'CHAIN';
      readonly count: number;
      readonly jumpRangeM: number;
      readonly pick: ChainPickV1;
      readonly only?: { readonly skillStatus: string };
    };

/** How a chain picks its next enemy among the eligible ones in range. */
export type ChainPickV1 = 'NEAREST' | 'RANDOM';
export const CHAIN_PICKS_V1 = Object.freeze(['NEAREST', 'RANDOM'] as const);

export const SPATIAL_FORMATION_KINDS_V1 = Object.freeze([
  'SINGLE', 'FAN', 'PARALLEL', 'RADIAL', 'RING', 'SCATTER', 'CHAIN',
] as const);

/** When the members of one volley are released relative to each other. */
export type MemberScheduleV1 =
  | { readonly kind: 'SIMULTANEOUS' }
  | { readonly kind: 'SEQUENTIAL'; readonly intervalTicks: number }
  | { readonly kind: 'STAGGERED'; readonly windowTicks: number; readonly seedPolicy: 'CAST_SEED' };

export const MEMBER_SCHEDULE_KINDS_V1 = Object.freeze([
  'SIMULTANEOUS', 'SEQUENTIAL', 'STAGGERED',
] as const);

/** How many times the whole stage repeats within one cast. Each volley issues
 * fresh attack identities, so three thrusts can hit one target three times. */
export type VolleyScheduleV1 =
  | { readonly kind: 'ONCE'; readonly count: 1 }
  | { readonly kind: 'REPEAT'; readonly count: number; readonly intervalTicks: number };

export const VOLLEY_SCHEDULE_KINDS_V1 = Object.freeze(['ONCE', 'REPEAT'] as const);

/**
 * One member's placement in a stage-local frame: `+y` is the forward axis,
 * `directionRad` is signed off that axis, and the origin is the stage anchor.
 * Turning this into a world position is the runtime's job, not the pattern's.
 */
export interface ExpandedTransform {
  readonly x: number;
  readonly y: number;
  readonly directionRad: number;
  /** The member stands at a fixed world position, whichever way the cast aims (`formationIsWorldFixed`). */
  readonly worldFixed?: true;
}

/** Formations laid down at a fixed world orientation instead of turning with the cast's aim. */
export const WORLD_FIXED_FORMATION_KINDS = Object.freeze(['SCATTER'] as const);

export function formationIsWorldFixed(kind: string): boolean {
  return (WORLD_FIXED_FORMATION_KINDS as readonly string[]).includes(kind);
}

export interface SpatialAttackPatternV1 {
  readonly schemaVersion: typeof SPATIAL_ATTACK_PATTERN_V1_SCHEMA_VERSION;
  readonly formation: SpatialFormationV1;
  readonly hitGrouping: HitGroupingV1;
  readonly memberSchedule: MemberScheduleV1;
  readonly volleys: VolleyScheduleV1;
}

/**
 * What a stage's clock reports to the stages that listen to it.
 *
 * - `HIT`: the unit's attack applied to a target (one application per swing,
 *   per zone pulse, per orbit rehit, per projectile contact).
 * - `KILL`: that application killed a target.
 * - `END`: the unit stopped existing (swing finished, projectile landed or
 *   expired, zone/orbit lifetime ended, instant resolved).
 * - `EVERY_N`: every `n`-th `HIT`, counted in the scope `per` names.
 */
export type StageEventV1 = 'HIT' | 'KILL' | 'END' | 'EVERY_N';
export const STAGE_EVENT_KINDS_V1 = Object.freeze(['HIT', 'KILL', 'END', 'EVERY_N'] as const);

/**
 * What an event stage counts, one rule for every motion. For `HIT` and `KILL`:
 *
 * - `HIT`: every contact of every unit with an enemy (unit × enemy). A fan of 3
 *   piercing bolts that each pass through 2 enemies makes 6.
 * - `UNIT`: once per attack unit, at its first contact (3 for that fan).
 * - `TARGET`: once per distinct enemy across the cast (2 for that fan).
 * - `CAST`: once per cast.
 *
 * (An enemy dies once, so `KILL` per `HIT` and per `TARGET` are the same.)
 * For `END`, which happens to a unit: `UNIT` is every unit's end, `CAST` the end of
 * the last instance of the source stage, including instances other events release
 * later; `HIT` and `TARGET` mean nothing there. For `EVERY_N`, `per` names whose
 * counter it is: one per `UNIT`, per `TARGET` (enemy) or per `CAST`; `HIT` has no
 * counter to name.
 */
export type StageEventPerV1 = 'HIT' | 'UNIT' | 'TARGET' | 'CAST';
export const STAGE_EVENT_PER_V1 = Object.freeze(['HIT', 'UNIT', 'TARGET', 'CAST'] as const);

/**
 * Which attacks of the reference stage an event stage responds to: only the
 * cast's own (primary) attacks, or any, follow-ups included. Stated on the
 * listener, not as a block on the attack; the cast's one attack budget and
 * generation limit are what end a chain. Absent means `ANY`.
 */
export type StageEventSourceV1 = 'PRIMARY' | 'ANY';
export const STAGE_EVENT_SOURCES_V1 = Object.freeze(['PRIMARY', 'ANY'] as const);

/** Where an event stage is anchored: the event point, the caster as it is now,
 * the aim the cast was released with, or — `MARKED`, a detonation of the reference
 * stage's marks — once at every living enemy carrying this cast's mark, at that enemy,
 * with every damage leaf multiplied by that enemy's stacks. `CARRIERS` is the same sweep over
 * the carriers of one of the cast's skill statuses, which it may consume and scale by.
 * `MARKED` is the sweep over a mark and keeps its own spelling. */
export type StageEventAnchorV1 = 'EVENT_POINT' | 'CASTER' | 'CAST_AIM' | 'MARKED' | 'CARRIERS';
export const STAGE_EVENT_ANCHORS_V1 = Object.freeze(['EVENT_POINT', 'CASTER', 'CAST_AIM', 'MARKED', 'CARRIERS'] as const);

/** What a skill status reports to the stages hooked to it: it was applied, its timer ticked, it ended. */
export type StatusHookEventV1 = 'APPLY' | 'TICK' | 'END';
export const STATUS_HOOK_EVENTS_V1 = Object.freeze(['APPLY', 'TICK', 'END'] as const);

/** How a sweep scales the stage it releases at a carrier: by one, or by the carrier's stacks. */
export type SweepStrengthV1 = 'ONE' | 'STACKS';
export const SWEEP_STRENGTHS_V1 = Object.freeze(['ONE', 'STACKS'] as const);

/**
 * When a stage releases. `AT_RELEASE`, `AFTER_RELEASE` and `AT_STAGE_FRACTION`
 * are resolvable at cast start; `ON_STAGE_EVENT` is resolved by what an
 * earlier stage's attack does, and the stage it names is a full stage — its own
 * application, formation, volleys and effects — released at the event.
 *
 * `AT_STAGE_FRACTION` anchors to an earlier stage's own clock and
 * `ON_STAGE_EVENT` to its events; both may only reference a stage authored
 * *earlier* in the list, which makes cycles unrepresentable rather than merely
 * rejected. Neither may reference an `ON_STAGE_EVENT` stage for a fraction,
 * because that stage has no start until its event happens.
 */
export type StageTimingV1 =
  | { readonly kind: 'AT_RELEASE' }
  | { readonly kind: 'AFTER_RELEASE'; readonly offsetTicks: number }
  | { readonly kind: 'AT_STAGE_FRACTION'; readonly referenceStageId: string; readonly fraction: number }
  | {
      readonly kind: 'ON_STAGE_EVENT';
      readonly referenceStageId: string;
      readonly event: StageEventV1;
      /** Present exactly for `EVERY_N`; an integer of at least 2. */
      readonly n?: number;
      readonly per: StageEventPerV1;
      readonly anchor: StageEventAnchorV1;
      /** Absent means `ANY`. */
      readonly source?: StageEventSourceV1;
      /** Present exactly for `MARKED`: the `markId` of the reference stage's `mark` leaf this stage detonates. */
      readonly markId?: string;
      /** Ticks between the event and the release (integer, at least 0); absent means 0. Each unit's own release offset comes on top. */
      readonly delayTicks?: number;
      /** Present exactly for `CARRIERS`: the cast's skill status whose carriers this stage sweeps. */
      readonly statusName?: string;
      /** Only with `CARRIERS`: the sweep ends the carrier's status with its last unit. Absent means it does not. */
      readonly consume?: boolean;
      /** Only with `CARRIERS`: absent means `ONE`. */
      readonly strength?: SweepStrengthV1;
    }
  | {
      /**
       * Released at the carrier of one of the cast's skill statuses, when that status is applied, ticks or ends.
       * It is a follow-up of the stage that created the status, so it is not part of the cast's authored work.
       */
      readonly kind: 'ON_STATUS';
      readonly statusName: string;
      readonly event: StatusHookEventV1;
      /** Ticks between the event and the release (integer, at least 0); absent means 0. */
      readonly delayTicks?: number;
    };

export const STAGE_TIMING_KINDS_V1 = Object.freeze([
  'AT_RELEASE', 'AFTER_RELEASE', 'AT_STAGE_FRACTION', 'ON_STAGE_EVENT', 'ON_STATUS',
] as const);

/**
 * One independent attack inside a cast. It owns its own application, the
 * effects that attack applies, its release timing and its spatial pattern.
 * It does NOT own cost, cooldown or the action lock — a cast pays those once,
 * however many stages it has.
 */
/**
 * How a stage's placed members move along a path over time: an orbit moves
 * the placement, a dash moves the caster's body to each member's goal.
 *
 * A separate axis from `application.motion`, which stays exactly the three
 * V2 kinds and keeps its meaning untouched (design §8 KEEP). This one says
 * how the PLACEMENT travels, and it carries only what nothing else owns
 * (for a dash: how far the caster may move, `rangeM`; the strike radius is
 * the application's circle):
 *
 * | fact | owner |
 * |---|---|
 * | ring radius, member count, initial phase | `pattern.formation.RING` |
 * | contact radius | `application.geometry` |
 * | rehit interval | `application.contactPolicy` |
 * | lifetime | `application.lifecycle` |
 * | centre anchor | `application.placement` |
 * | **angular speed** | **here, and nowhere else** |
 *
 * Revolutions per second and period are derived and deliberately not
 * stored: two fields for one fact drift.
 */
export type StageMotionV1 =
  /** Signed: the sign is the direction of travel. */
  | { readonly kind: 'ORBIT'; readonly angularSpeedRadPerS: number }
  /**
   * The caster's body travels with each member: before a member strikes, the caster moves toward the member's goal, at
   * most `rangeM`, and the member's application (a skill-owned instant circle at the CASTER) strikes where it landed.
   * The move is the stage's; what the strike reaches and applies stays the application's, so a dash's damage, contact and
   * effects come from the one V2 compiler like every other stage's.
   */
  | { readonly kind: 'DASH'; readonly rangeM: number };

export const STAGE_MOTION_KINDS_V1 = Object.freeze(['ORBIT', 'DASH'] as const);

export interface AttackStageV1 {
  readonly stageId: string;
  readonly timing: StageTimingV1;
  readonly application: SkillApplicationV2;
  readonly pattern: SpatialAttackPatternV1;
  readonly effects: readonly CanonicalEffectNodeV2[];
  /** Absent means the placement does not travel — every stage authored
   * before this field existed keeps exactly its old meaning. */
  readonly motion?: StageMotionV1;
}

/**
 * One status a skill names for itself. It is local to the skill and to one cast: its definition id is
 * `skill:<skillId>:<name>`, and two casts never share an instance.
 *
 * - `durationS`: seconds; exclusive with `until`.
 * - `until`: ends when that stage's work settles.
 * - neither: lives until a sweep consumes it, or until the cast's authored work completes.
 * - `stacks`: present, each contact adds a stack and refreshes the lifetime (`max` bounds them);
 *   absent, a contact only refreshes it.
 * - `tickS`: a payload-free timer, the source of the `TICK` hook events.
 */
export interface SkillStatusDefinitionV1 {
  readonly name: string;
  readonly durationS?: number;
  readonly until?: { readonly stageId: string };
  readonly stacks?: { readonly max?: number };
  readonly tickS?: number;
}

/**
 * The composite canonical form: the V2 skill with its single root
 * `application`/`effects` pair replaced by an ordered list of stages.
 * Everything else about a V2 skill — id, cadence, costs, source requirement,
 * mastery, provenance — is carried through untouched.
 */
export type CanonicalSkillV3Input =
  Omit<CanonicalSkillV2Input, 'schemaVersion' | 'application' | 'effects'> & {
    readonly schemaVersion: typeof CANONICAL_SKILL_V3_SCHEMA_VERSION;
    readonly stages: readonly AttackStageV1[];
    /** The skill's own statuses. Absent means none: every skill authored before they existed keeps its meaning. */
    readonly statuses?: readonly SkillStatusDefinitionV1[];
  };

/** Only `validateCompositeSkill` can create this nominal boundary, mirroring
 * how `CanonicalSkillV2` is gated by its own validator. */
export type CanonicalSkillV3 = CanonicalSkillV3Input & { readonly [validatedCanonicalSkillV3]: true };

/** The stage id the V2→V3 upgrade gives a legacy root. Stable and reserved:
 * every migrated single-stage skill uses this exact spelling, so replay and
 * telemetry can recognise a non-composite cast without reparsing. */
export const PRIMARY_STAGE_ID = 'primary';

/** The degenerate pattern a non-composite skill has: one member, one volley,
 * union grouping. Behaviourally identical to having no pattern at all. */
export const SINGLE_ATTACK_PATTERN_V1: SpatialAttackPatternV1 = Object.freeze({
  schemaVersion: SPATIAL_ATTACK_PATTERN_V1_SCHEMA_VERSION,
  formation: Object.freeze({ kind: 'SINGLE', count: 1 }),
  hitGrouping: 'SHARED_UNION',
  memberSchedule: Object.freeze({ kind: 'SIMULTANEOUS' }),
  volleys: Object.freeze({ kind: 'ONCE', count: 1 }),
});

/**
 * True when a stage resolves to exactly one attack unit at one moment — one
 * member, one volley, released with the cast. Such a stage is behaviourally a
 * plain V2 root, which is what lets a migrated skill keep taking the existing
 * compiled path unchanged.
 */
export function isDegenerateStage(stage: AttackStageV1): boolean {
  return (
    // A travelling stage is never degenerate, however plain its formation
    // and timing look. A single orbiting orb is SINGLE + AT_RELEASE + ONCE
    // + SIMULTANEOUS, so without this it would be lowered to V2 and the
    // orbit would silently disappear — the P5 "open by omission" shape.
    stage.motion === undefined &&
    stage.timing.kind === 'AT_RELEASE' &&
    stage.pattern.formation.kind === 'SINGLE' &&
    stage.pattern.volleys.kind === 'ONCE' &&
    stage.pattern.memberSchedule.kind === 'SIMULTANEOUS'
  );
}

/** True when the whole skill is a migrated/authored single plain attack and
 * therefore needs no composite capability to execute. */
export function isDegenerateCompositeSkill(skill: CanonicalSkillV3Input): boolean {
  return skill.stages.length === 1 && isDegenerateStage(skill.stages[0]!) && (skill.statuses?.length ?? 0) === 0;
}
